import React, { useEffect, useState } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { requestApproval, fetchOrder } from '../../slices/orders.slice';
import { fetchOrganizationContent } from '../../slices/organizations.slice';
import Loading from '../../components/Loading';
import { imgUrl } from '../../utils/api-request';
import OrderStatusBadge from '../../components/OrderStatusBadge';
import { formatCredits } from '../../utils/credits';
import { toast } from 'react-toastify';
import PurchaseHistory from '../../components/PurchaseHistory';
import PurchaseTimeline from '../../components/PurchaseTimeline';
import PurchaseTerms from '../../components/PurchaseTerms';
import PurchaseSelector from '../../components/PurchaseSelector';
import PlayButton from '../Movies/MoviePage/ActionButton/PlayButton';
import PlayingBanner from '../../components/PlayingBanner';
import HireCountdown from '../../components/HireCountdown';

const OrderDetailsOrganization = () => {
    const { orderId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { currentOrder: order, status } = useSelector((state) => state.orders);
    const [searchParams, setSearchParams] = useSearchParams();
    const { organization, organizationContent } = useSelector((state) => state.organizations);
    const { playing } = useSelector((state) => state.playMovie);
    const [approvalModal, setApprovalModal] = useState({ isOpen: false, operation: null });

    const handleRequestApproval = () => {
        let promise;
        if (approvalModal.operation === 'approve') {
            const body = {};
            promise = dispatch(requestApproval({ orderId, operation: 'approve', body })).unwrap();
        }

        if (approvalModal.operation === 'decline') {
            const body = {};
            promise = dispatch(requestApproval({ orderId, operation: 'decline', body })).unwrap();
        }

        void toast.promise(promise, {
            pending: 'Updating order',
            success: 'Order updated successfully',
            error: 'Error updating order',
        });
        setApprovalModal({ isOpen: false, operation: null });
    };

    const toggleApprovalModal = operation => {
        setApprovalModal(prevState => ({
            isOpen: !prevState.isOpen,
            operation: prevState.isOpen ? null : operation
        }));
    };

    useEffect(() => {
        dispatch(fetchOrder(orderId));
    }, [dispatch, orderId]);

    useEffect(() => {
        if (organization?._id) {
            dispatch(fetchOrganizationContent({ orgId: organization._id }));
        }
    }, [dispatch, organization?._id]);

    const isPlayable = order && order.orderType === 'order' &&
        ['ready-on-mediablock', 'finished-playback'].includes(order.statusWorkflow);

    if (['idle', 'loading'].includes(status)) {
        return <Loading fullHeight />;
    }

    // This document is reused for every booking of this title, so the panels
    // below describe ONE purchase — the latest by default, or whichever
    // `?purchase=N` names when arriving from a Past Orders row.
    const purchases = order?.purchases || [];
    // Not Number(get(...)) — an absent param is null, and Number(null) is 0,
    // which would silently open the FIRST purchase instead of the latest.
    const requested = searchParams.has('purchase') ? Number(searchParams.get('purchase')) : null;
    const selectedIndex = purchases.some(p => p.index === requested) ? requested : purchases.length - 1;
    const currentPurchase = purchases[selectedIndex] || null;
    // Accepting or declining writes to the document, which only ever holds the
    // latest purchase — never offer it while an earlier one is on screen.
    const isLatestPurchase = selectedIndex === purchases.length - 1;

    const selectPurchase = (index) => {
        const params = new URLSearchParams(searchParams);
        if (index === purchases.length - 1) params.delete('purchase');
        else params.set('purchase', index);
        setSearchParams(params, { replace: true });
    };

    return status === 'success' && order && (
        <div className="order-view page p-4">
            {/* Back Navigation */}
            <div className="mb-3">
                <NavLink onClick={() => navigate(-1)} className="text-decoration-none text-white">
                    <span className="fas fa-chevron-left me-2" />
                    <span>Back to Orders</span>
                </NavLink>
            </div>

            {/* Main Order Card */}
            <div className="bg-broken border-start border-primary border-4 p-3 mb-4 shadow-sm">
                <Row className="g-3 align-items-center">
                    {/* Movie Poster */}
                    <Col md="auto">
                        {order.movie.poster ? (
                            <img
                                className="rounded shadow"
                                style={{ width: '120px', height: '180px', objectFit: 'cover' }}
                                alt={order.movie.title}
                                src={imgUrl(order.movie.poster.imageUrl)}
                            />
                        ) : (
                            <img
                                className="rounded opacity-25"
                                style={{ width: '120px', height: '180px', objectFit: 'cover' }}
                                alt={order.movie.title}
                                src={`https://picsum.photos/seed/${order.movie.title}/120/180`}
                            />
                        )}
                    </Col>

                    {/* Movie & Order Info */}
                    <Col>
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <span className="badge bg-secondary text-uppercase px-2 py-1">
                                {order.orderType}
                            </span>
                            <OrderStatusBadge status={order.status} isCard className="d-inline-flex" />
                        </div>

                        <h2 className="text-white mb-1" style={{ fontSize: '1.75rem', fontWeight: '600' }}>
                            {order.movie.title}
                        </h2>
                        <div className="text-white-50 mb-3" style={{ fontSize: '0.95rem' }}>
                            {order.version.name}
                        </div>
                    </Col>

                    {isPlayable && (
                        <Col md="auto" className="text-end">
                            <PlayButton
                                order={order}
                                label="PLAY MOVIE"
                                organization={organization}
                                organizationContent={organizationContent}
                            />
                            <HireCountdown order={order} />
                        </Col>
                    )}
                </Row>
            </div>

            <Row className="g-3">
                {/* Order Details Section */}
                <Col lg={6}>
                    <div className="bg-broken border-start border-primary border-3 p-3 h-100 shadow-sm">
                        <h5 className="text-primary mb-3 d-flex align-items-center">
                            <span className="fas fa-info-circle me-2" />
                            Order Details
                        </h5>

                        <PurchaseSelector
                            purchases={purchases}
                            selectedIndex={selectedIndex}
                            onSelect={selectPurchase}
                        />

                        <Row className="g-3">
                            <Col md={6}>
                                <PurchaseTerms purchase={currentPurchase} version={order.version} />
                            </Col>

                            <Col md={6}>
                                <div className="mb-3">
                                    <div className="text-white-50 small mb-1">Purchased</div>
                                    <div className="text-white" style={{ fontSize: '0.85rem' }}>
                                        {currentPurchase?.purchasedAt
                                            ? new Date(currentPurchase.purchasedAt).toLocaleString()
                                            : '—'}
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <div className="text-white-50 small mb-1">Last Updated</div>
                                    <div className="text-white" style={{ fontSize: '0.85rem' }}>
                                        {new Date(order.updatedAt).toLocaleString()}
                                    </div>
                                </div>

                                {order.version.assetDetails && (
                                    <>
                                        <div className="mb-3">
                                            <div className="text-white-50 small mb-1">Format</div>
                                            <div className="text-white">
                                                <span className="badge bg-secondary me-1">{order.version.assetDetails.resolution}</span>
                                                <span className="badge bg-secondary me-1">{order.version.assetDetails.dimensions}</span>
                                                <span className="badge bg-secondary">{order.version.assetDetails.ratio}</span>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <div className="text-white-50 small mb-1">Audio</div>
                                            <div className="text-white">
                                                <span className="badge bg-secondary me-1">{order.version.assetDetails.audioConfig}</span>
                                                {order.version.assetDetails.audioLanguage?.length > 0 && (
                                                    <span className="badge bg-secondary">{order.version.assetDetails.audioLanguage.join(', ').toUpperCase()}</span>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </Col>
                        </Row>

                        {currentPurchase?.terms && (
                            <div className="mb-0">
                                <div className="text-white-50 small mb-1">Screening Terms</div>
                                <div className="p-3 bg-black bg-opacity-50 rounded text-white" style={{ fontSize: '0.9rem' }}>
                                    <p>
                                        <span className="fas fa-quote-left me-2 text-primary" />
                                        <i>"{currentPurchase.terms}"</i>
                                    </p>
                                    {/* firstName, not the object: an unpopulated
                                        id is truthy and would print "undefined". */}
                                    {currentPurchase.approvedBy?.firstName && (
                                        <p className="text-primary pt-2 mb-0">
                                            Approved by {currentPurchase.approvedBy.firstName} {currentPurchase.approvedBy.lastName}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {order.status === 'request-accepted' && isLatestPurchase && (
                            <div className="d-flex mt-3 justify-content-center gap-3">
                                <Button color="success" onClick={() => toggleApprovalModal('approve')} className="px-4">
                                    <span className="fas fa-check-circle me-2" />
                                    Accept Terms
                                </Button>
                                <Button color="danger" outline onClick={() => toggleApprovalModal('decline')} className="px-4">
                                    <span className="fas fa-times-circle me-2" />
                                    Decline
                                </Button>
                            </div>
                        )}

                        {playing && <PlayingBanner />}
                    </div>
                </Col>

                {/* Activity Timeline */}
                <Col lg={6}>
                    <div className="bg-broken border-start border-primary border-3 p-3 h-100 shadow-sm">
                        <h5 className="text-primary mb-3 d-flex align-items-center">
                            <span className="fas fa-history me-2" />
                            Activity Timeline
                        </h5>

                        <PurchaseTimeline purchase={currentPurchase} orderType={order.orderType} />
                    </div>
                </Col>
            </Row>

            {/* Transactions Section */}
            {(order.revertTransaction || order.bacCorrectionTransaction || order.downpaymentTransaction) && (
                <div className="mt-3">
                    <h5 className="text-primary mb-3 text-uppercase">
                        <span className="fas fa-list-check me-2" />
                        Transactions
                    </h5>

                    <div className="transactions-list">
                        {order.revertTransaction && (
                            <div className={`transaction-item bg-broken px-3 py-2 border-start border-end mb-2 pointer justify-content-between align-items-center ${order.revertTransaction.transactionType} d-flex ${order.revertTransaction.transactionType === 'credit' ? 'border-success' : 'border-danger'}`}>
                                <div className="text-uppercase">
                                    <strong>
                                        <span className="fas fa-file-text me-2 text-primary" />
                                        {order.revertTransaction.description}
                                    </strong>
                                    <br />
                                    <small className="text-white-50">{new Date(order.revertTransaction.effectiveDate).toLocaleString()}</small>
                                </div>

                                <div className="ba-transaction small text-white">
                                    {formatCredits(order.revertTransaction.balanceBeforeTransaction)}
                                    <span className="text-primary mx-2"> | </span>
                                    <span className="text-primary fas fa-arrow-right me-2" />
                                    {formatCredits(order.revertTransaction.balanceAfterTransaction)}
                                </div>

                                <span className="amount lead fw-bold">
                                    <span className={`text-uppercase p-1 small ${order.revertTransaction.transactionType === 'credit' ? 'text-success' : 'text-danger'} me-2`}>
                                        <span className={`fas me-2 ${order.revertTransaction.transactionType === 'credit' ? 'fa-plus' : 'fa-minus'}`} />
                                        {formatCredits(order.revertTransaction.amount)}
                                    </span>
                                </span>
                            </div>
                        )}

                        {order.bacCorrectionTransaction && (
                            <div className={`transaction-item bg-broken px-3 py-2 border-start border-end mb-2 pointer justify-content-between align-items-center ${order.bacCorrectionTransaction.transactionType} d-flex ${order.bacCorrectionTransaction.transactionType === 'credit' ? 'border-success' : 'border-danger'}`}>
                                <div className="text-uppercase">
                                    <strong>
                                        <span className="fas fa-file-text me-2 text-primary" />
                                        {order.bacCorrectionTransaction.description}
                                    </strong>
                                    <br />
                                    <small className="text-white-50">{new Date(order.bacCorrectionTransaction.effectiveDate).toLocaleString()}</small>
                                </div>

                                <div className="ba-transaction small text-white">
                                    {formatCredits(order.bacCorrectionTransaction.balanceBeforeTransaction)}
                                    <span className="text-primary mx-2"> | </span>
                                    <span className="text-primary fas fa-arrow-right me-2" />
                                    {formatCredits(order.bacCorrectionTransaction.balanceAfterTransaction)}
                                </div>

                                <span className="amount lead fw-bold">
                                    <span className={`text-uppercase p-1 small ${order.bacCorrectionTransaction.transactionType === 'credit' ? 'text-success' : 'text-danger'} me-2`}>
                                        <span className={`fas me-2 ${order.bacCorrectionTransaction.transactionType === 'credit' ? 'fa-plus' : 'fa-minus'}`} />
                                        {formatCredits(order.bacCorrectionTransaction.amount)}
                                    </span>
                                </span>
                            </div>
                        )}

                        {order.downpaymentTransaction && (
                            <div className={`transaction-item bg-broken px-3 py-2 border-start border-end mb-2 pointer justify-content-between align-items-center ${order.downpaymentTransaction.transactionType} d-flex ${order.downpaymentTransaction.transactionType === 'credit' ? 'border-success' : 'border-danger'}`}>
                                <div className="text-uppercase">
                                    <strong>
                                        <span className="fas fa-file-text me-2 text-primary" />
                                        {order.downpaymentTransaction.description}
                                    </strong>
                                    <br />
                                    <small className="text-white-50">{new Date(order.downpaymentTransaction.effectiveDate).toLocaleString()}</small>
                                </div>

                                <div className="ba-transaction small text-white">
                                    {formatCredits(order.downpaymentTransaction.balanceBeforeTransaction)}
                                    <span className="text-primary mx-2"> | </span>
                                    <span className="text-primary fas fa-arrow-right me-2" />
                                    {formatCredits(order.downpaymentTransaction.balanceAfterTransaction)}
                                </div>

                                <span className="amount lead fw-bold">
                                    <span className={`text-uppercase p-1 small ${order.downpaymentTransaction.transactionType === 'credit' ? 'text-success' : 'text-danger'} me-2`}>
                                        <span className={`fas me-2 ${order.downpaymentTransaction.transactionType === 'credit' ? 'fa-plus' : 'fa-minus'}`} />
                                        {formatCredits(order.downpaymentTransaction.amount)}
                                    </span>
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Purchase History — every time this cinema has booked this title */}
            {purchases.length > 0 && (
                <div className="mt-3">
                    <h5 className="text-primary mb-3 text-uppercase">
                        <span className="fas fa-history me-2" />
                        Purchase History
                        <span className="text-white-50 ms-3 text-lowercase" style={{ fontSize: '0.85rem' }}>
                            you have booked this title {purchases.length} time{purchases.length === 1 ? '' : 's'}
                        </span>
                    </h5>
                    <PurchaseHistory
                        purchases={purchases}
                        isBAC={false}
                        selectedIndex={selectedIndex}
                        onSelect={selectPurchase}
                        status={order.statusWorkflow}
                    />
                </div>
            )}

            <div className="pb-5" />

            <Modal isOpen={approvalModal.isOpen} toggle={toggleApprovalModal}>
                <ModalHeader toggle={toggleApprovalModal} className="text-primary">
                    {approvalModal.operation === 'approve' ? 'Accept proposal' : 'Decline proposal'}
                </ModalHeader>
                <ModalBody>
                    {approvalModal.operation === 'approve' && (
                        <div>
                            <p className="text-primary mb-3">Your request was accepted under the following conditions:</p>

                            {order.screeningTerms && (
                                <div className="mb-0">
                                    <div className="text-white-50 small mb-1">Screening Terms</div>
                                    <div className="p-3 bg-black bg-opacity-50 rounded text-white" style={{ fontSize: '0.9rem' }}>
                                        <p>
                                            <span className="fas fa-quote-left me-2 text-primary" />
                                            <i>"{order.screeningTerms}"</i>
                                        </p>
                                        {order.approvedBy && (
                                            <p className="text-primary pt-2 mb-0">
                                                Approved by {order.approvedBy.firstName} {order.approvedBy.lastName}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {order.bacCorrection && (
                                <div className="border-start border-primary bg-black bg-opacity-50 my-3 p-3">
                                    {order.bacCorrection.screeningDateCorrection && (
                                        <div className="mb-2">
                                            <span className="fas fa-calendar-check me-2 text-primary" />
                                            <strong>Screening date:</strong> {new Date(order.bacCorrection.screeningDateCorrection).toLocaleString()}
                                        </div>
                                    )}

                                    {order.bacCorrection.defaultHirePeriodCorrection && (
                                        <div className="mb-2">
                                            <span className="fas fa-clock me-2 text-primary" />
                                            <strong>Hire period:</strong> {order.bacCorrection.defaultHirePeriodCorrection} {order.bacCorrection.hirePeriodTypeCorrection}
                                        </div>
                                    )}

                                    {order.bacCorrection.priceCorrection !== undefined && order.bacCorrection.priceCorrection !== null && order.bacCorrection.priceCorrection !== 0 && (
                                        <>
                                            <div className="mb-2">
                                                <span className="fas fa-coins me-2 text-primary" />
                                                <strong>Price correction:</strong>{' '}
                                                <span className={order.bacCorrection.priceCorrection > 0 ? 'text-danger' : 'text-success'}>
                                                    {order.bacCorrection.priceCorrection > 0 ? '+' : ''}{formatCredits(order.bacCorrection.priceCorrection)} credits
                                                </span>
                                            </div>

                                            <div className="mb-0">
                                                <span className="fas fa-cash-register me-2 text-primary" />
                                                <strong>Final price:</strong> {formatCredits(order.downpayment + (Number(order.bacCorrection.priceCorrection) || 0))} credits
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {approvalModal.operation === 'decline' && (
                        <div>
                            <p className="text-danger mb-3">Are you sure you want to decline this proposal?</p>
                            <div className="alert alert-warning">
                                <span className="fas fa-info-circle me-2" />
                                Your downpayment will be refunded to your account.
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color={approvalModal.operation === 'approve' ? 'success' : 'danger'}
                        outline
                        onClick={handleRequestApproval}
                    >
                        <span
                            className={`fas ${approvalModal.operation === 'approve' ? 'fa-check-circle' : 'fa-times-circle'} me-2`} />
                        {approvalModal.operation === 'approve' ? 'Accept' : 'Decline'}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default OrderDetailsOrganization;
